/**
 * Helper functions extracted from V2CollectionProducts.vue
 * to reduce code complexity and improve readability.
 *
 * These are pure/stateless utilities — they receive data as arguments
 * and return results without depending on Vue reactive state.
 */

import { setFilter } from '@/common-utils'
import { formatObject } from '@core/utils/utils'
import analytics, { getCollectionMixpanelObj } from '@/@core/utils/analytics'
import constants from '@/constants'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { buildColorEntries } from '@/utils/colorEntries'
import { isCollectionNotFoundError } from '@/utils/collectionNotFound'
import { STALE_RESPONSE } from './useCollectionAvailability'

export { buildColorEntries }

const { ROLES } = c
const { TRACKS } = constants

/**
 * Round and adjust price boundaries in filter options.
 * PriceMax keys get +1, PriceMin keys get -1.
 */
export function adjustPriceBoundary(key, value) {
  if (key.indexOf('PriceMax') > -1) {
    return Math.round(value) + 1
  }
  if (key.indexOf('PriceMin') > -1) {
    return Math.round(value) - 1
  }
  return value
}

/**
 * Initialize filter arrays and adjust price boundaries
 * for all keys in the raw filter options from the API.
 */
export function initializeFilterOptions(rawOptions, filters) {
  for (const key in rawOptions) {
    if (rawOptions.hasOwnProperty(key)) {
      filters[key] = []
      rawOptions[key] = adjustPriceBoundary(key, rawOptions[key])
    }
  }
}

/**
 * Set up derived filter properties (price ranges, availability dates, groups)
 * from the processed filter options. Returns an object with the derived values.
 */
export function buildDerivedFilterState(filterOptions) {
  const derived = {
    availabilityDates: [],
    retailPrice: [filterOptions.retailPriceMin, filterOptions.retailPriceMax],
    wholesalePrice: [filterOptions.wholesalePriceMin, filterOptions.wholesalePriceMax],
    groups: filterOptions.groupBy,
    initialWholesalePriceMax: filterOptions.wholesalePriceMax,
    initialWholesalePriceMin: filterOptions.wholesalePriceMin,
    initialRetailPriceMax: filterOptions.retailPriceMax,
    initialRetailPriceMin: filterOptions.retailPriceMin,
  }
  if (filterOptions.availabilityDates?.length) {
    derived.availabilityDates = filterOptions.availabilityDates.filter(date => date)
  }
  return derived
}

/**
 * Remove unchanged price filter values to avoid sending
 * redundant params to the API.
 */
export function stripUnchangedPriceFilters(filters, priceState) {
  if (priceState.initialWholesalePriceMax === filters.wholesalePriceMax) delete filters.wholesalePriceMax
  if (priceState.initialWholesalePriceMin === filters.wholesalePriceMin) delete filters.wholesalePriceMin
  if (priceState.initialRetailPriceMax === filters.retailPriceMax) delete filters.retailPriceMax
  if (priceState.initialRetailPriceMin === filters.retailPriceMin) delete filters.retailPriceMin
}

/**
 * Extract unique product colors from a product's items array.
 * Mutates product.colors in place for compatibility.
 */
export function extractProductColors(product) {
  const productColors = []
  const seenColors = []
  product.quantity = product.quantity || 1
  product.items.forEach(item => {
    if (seenColors.indexOf(item.color) < 0) {
      seenColors.push(item.color)
      productColors.push({
        color: item.color,
        colorFamily: item.colorFamily,
        hexCode: item.hexCode,
      })
    }
  })
  product.colors = productColors
}

/**
 * Process all grouped products from the API response:
 * extract colors and count products.
 * Returns the total products processed.
 */
export function processGroupedProducts(groupedProducts, startCount) {
  let count = startCount
  groupedProducts.forEach(groupedProduct => {
    groupedProduct.products.forEach(product => {
      count++
      extractProductColors(product)
    })
  })
  return count
}

/**
 * Merge paginated product results into the existing products array.
 * Handles the case where the last group on the previous page is
 * the same as the first group on the new page.
 */
export function mergePagedProducts(existingProducts, newProducts) {
  if (
    newProducts.length
    && existingProducts[existingProducts.length - 1]._id === newProducts[0]._id
  ) {
    existingProducts[existingProducts.length - 1].products = existingProducts[existingProducts.length - 1].products.concat(
      newProducts[0].products,
    )
    newProducts.splice(0, 1)
  }
  return [...existingProducts, ...newProducts]
}

/**
 * Track collection open analytics events (first load only).
 */
export function trackCollectionOpenEvents(isBrand, result, collectionData) {
  analytics.track(
    isBrand
      ? TRACKS.PAGES.BRAND_OPENS_COLLECTION
      : TRACKS.PAGES.RETAILER_OPENS_COLLECTION,
    formatObject(getCollectionMixpanelObj(result)),
  )
  analytics.track(
    isBrand
      ? TRACKS.ACTIONS.BRAND_START_VIEWING_COLLECTION
      : TRACKS.ACTIONS.RETAILER_START_VIEWING_COLLECTION,
    formatObject(collectionData),
  )
}

/**
 * Track the wishlist toggle analytics event for a retailer.
 */
export function trackWishlistToggle(isAdding, collectionName) {
  const action = isAdding
    ? TRACKS.ACTIONS.RETAILER_ADDS_TO_WISHLIST
    : TRACKS.ACTIONS.RETAILER_REMOVES_FROM_WISHLIST
  analytics.track(
    action,
    formatObject({
      collectionName,
      createdBy: ROLES.RETAILER,
    }),
  )
}

/**
 * Build the fetch params object from component state.
 * Merges sort, pagination, profile flags, wishlist/retailer context,
 * user-applied filters, and group/collection identifiers.
 */
function buildOptionalParams({
 profileValue, selectedWishlistIdValue, skipWishlist, directRetailerIdValue, collectionId,
}) {
  const params = {}
  if (profileValue?.isDemo) params.isDemo = true
  if (selectedWishlistIdValue && !skipWishlist) params.wishlistId = selectedWishlistIdValue
  if (directRetailerIdValue) params.retailerId = directRetailerIdValue
  if (collectionId) params.collectionId = collectionId
  return params
}

function expandFilters(filtersValue) {
  const expanded = {}
  Object.keys(filtersValue).forEach(key => {
    const value = filtersValue[key]
    expanded[key] = Array.isArray(value) && value.length
      ? value.join(',')
      : value
  })
  return expanded
}

export function buildFetchParams({
  sortByValue,
  filtersValue,
  profileValue,
  selectedWishlistIdValue,
  skipWishlist,
  directRetailerIdValue,
  selectedGroupValue,
  defaultGroupValue,
  collectionId,
}) {
  return {
    sortBy: sortByValue,
    page: filtersValue.page,
    ...buildOptionalParams({
 profileValue, selectedWishlistIdValue, skipWishlist, directRetailerIdValue, collectionId,
}),
    ...expandFilters(filtersValue),
    groupBy: selectedGroupValue || defaultGroupValue,
  }
}

/**
 * Apply filter menu updates from a filter API response.
 * Updates filterOptions fields and availability dates in-place via callbacks.
 */
const FILTER_MENU_FIELDS = [
  'lines',
  'departments',
  'categories',
  'subCategories',
  'gender',
  'colors',
  'sizes',
  'subLines',
  'groups',
  'subGroups',
  'keyStyles',
  'segmentTags',
]

export function applyFilterMenuUpdates(filterMenus, filterOptionsValue, fetchParam, filterByAvailabilityDatesValue) {
  if (filterMenus === undefined) return { groups: null, filterByAvailabilityDates: null }

  const updatedFilterOptions = {}
  FILTER_MENU_FIELDS.forEach(field => {
    updatedFilterOptions[field] = setFilter(
      filterOptionsValue[field],
      fetchParam[field],
      filterMenus[field],
    )
  })

  const updatedAvailabilityDates = setFilter(
    filterByAvailabilityDatesValue,
    fetchParam.availabilityDates,
    filterMenus.availabilityDates,
  )

  return {
    updatedFilterOptions,
    updatedAvailabilityDates,
    groups: filterMenus.groupBy,
  }
}

/**
 * Apply derived filter state to mutable refs and local variables.
 * Returns the initial price boundaries for later comparison.
 */
export function applyDerivedFilterState(derived, filterOptionsValue) {
  filterOptionsValue.retailPrice = derived.retailPrice
  filterOptionsValue.wholesalePrice = derived.wholesalePrice
  return {
    groups: derived.groups,
    initialWholesalePriceMax: derived.initialWholesalePriceMax,
    initialWholesalePriceMin: derived.initialWholesalePriceMin,
    initialRetailPriceMax: derived.initialRetailPriceMax,
    initialRetailPriceMin: derived.initialRetailPriceMin,
  }
}

/**
 * Extract result metadata from a fetch response.
 * Returns a plain object with the metadata fields.
 */
export function extractResultMetadata(result) {
  return {
    isCollectionExpired: result.hasOwnProperty('isCollectionExpired')
      ? result.isCollectionExpired
      : undefined,
    connectionRequestStatus: (result.hasOwnProperty('connectionRequestStatus')
        && result.connectionRequestStatus)
      || null,
    isWspHidden: result.hasOwnProperty('isWspHidden') && result.isWspHidden,
    isBrandNotAccessible: result.hasOwnProperty('isBrandNotAccessible')
      && result.isBrandNotAccessible,
  }
}

/**
 * Handle the result of getFullSelectedProducts dispatch.
 * Returns { cartProducts, shouldHideModal, warningMessage }.
 */
export function processFullSelectedProductsResult(data, isBrand) {
  if (!Array.isArray(data) || !data.length) {
    const warningMessage = isBrand
      ? 'This product is hidden for the selected retailer'
      : 'No products found'
    return { cartProducts: [], shouldHideModal: isBrand, warningMessage }
  }
  return { cartProducts: data, shouldHideModal: false, warningMessage: null }
}

/**
 * Resolve getFullSelectedProducts parameters from raw input.
 * Returns { retailerId, productIds, resolvedProductIds }.
 */
export function resolveFullSelectedProductsParams(params, fallbackIndexes) {
  const retailerId = params.retailerId || null
  const productIds = params.productIds || []
  const resolvedProductIds = productIds.length ? productIds : fallbackIndexes
  return { retailerId, productIds, resolvedProductIds }
}

/**
 * Extract cart products from a fetch-cart-products API response.
 * Returns the products array if valid, or null otherwise.
 */
export function extractCartProductsFromResponse(res) {
  const data = res?.data?.data?.products
  if (Array.isArray(data) && data.length) {
    return data
  }
  return null
}

/**
 * Page state after a failed products fetch. `state` holds the page's refs.
 * KP-4621: a 404 sets isCollectionUnavailable from the message ("Collection not found":
 * an unknown or fully segmented collection; anything else, e.g. "Product not found", is an
 * empty page or filter). Other errors, and responses superseded by a newer request
 * (STALE_RESPONSE), leave the page as it is. collectionData is kept, so a brand on View as
 * Retailer keeps its collection name and currency selector.
 */
export function applyProductsFetchError(err, state) {
  if (err === STALE_RESPONSE || err?.response?.status !== 404) return
  state.isCollectionUnavailable.value = isCollectionNotFoundError(err)
  // A 404 means nothing matches, so there is no next page. Leaving this
  // on lets ensureViewportFilled fetch again after the list is emptied,
  // and every 404 empties it again — an endless request/toast loop.
  state.continueLoading.value = false
  state.products.value = []
  state.totalProducts.value = 0
  state.isLoading.value = false
  state.isLoaded.value = true
}
